import React, { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { getStorage, STORAGE_KEYS } from "../data/storage";
import { supabase, supabaseConfigured } from "../supabase/client";
import { pushEntries, isDemoDataset } from "../supabase/sync";
import { LoginModal } from "../supabase/LoginModal";
import { getOuraConnection, type OuraConnection } from "../supabase/oura";
import type { GEntry } from "../types/entries";

type BurnInputMode = "total" | "active";
type SaveState = "idle" | "loading" | "saved" | "error";

type Props = { userEmail?: string | null };

function formatWholeCalories(value: number | null | undefined) {
  return value != null && Number.isFinite(value) ? String(Math.round(value)) : "";
}

function hasLoggedBurn(entry: GEntry & { tdeeSource?: string | null }) {
  const inferredBySource = entry.totalBurnedSource === "inferred"
    || entry.activeCaloriesSource === "inferred"
    || entry.tdeeSource === "fromActivityFactor";
  if (inferredBySource) return false;
  return entry.totalBurned != null || entry.activeCalories != null;
}

function formatPremiumDate(ymd: string): { weekday: string; monthDay: string } {
  const d = new Date(`${ymd}T12:00:00`);
  if (Number.isNaN(d.getTime())) return { weekday: "--", monthDay: "--" };
  const weekday = new Intl.DateTimeFormat("en-US", { weekday: "short" }).format(d);
  const monthDay = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" }).format(d);
  return { weekday, monthDay };
}

function shiftDay(baseYmd: string, delta: number) {
  const [y, m, d] = baseYmd.split("-").map(Number);
  const date = new Date(y, m - 1, d + delta);
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("-");
}

function getTodayYmd() {
  const d = new Date();
  return [
    d.getFullYear(),
    String(d.getMonth() + 1).padStart(2, "0"),
    String(d.getDate()).padStart(2, "0"),
  ].join("-");
}

function parseOptionalPositiveFloat(raw: string): number | null {
  const s = raw.trim();
  if (!s) return null;
  const n = parseFloat(s);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function parseOptionalPositiveInt(raw: string): number | null {
  const s = raw.trim();
  if (!s) return null;
  const n = parseFloat(s);
  return Number.isFinite(n) && n > 0 ? Math.round(n) : null;
}

export function DailyEntryMobile({ userEmail }: Props) {
  const [date, setDate] = useState(getTodayYmd());
  const dateInputRef = useRef<HTMLInputElement>(null);
  const caloriesInInputRef = useRef<HTMLInputElement>(null);
  const burnInputRef = useRef<HTMLInputElement>(null);
  const [weight, setWeight] = useState("");
  const [caloriesIn, setCaloriesIn] = useState("");
  const [mode, setMode] = useState<BurnInputMode>("total");
  const [burnValue, setBurnValue] = useState("");
  const [saveState, setSaveState] = useState<SaveState>("idle");
  const [saveError, setSaveError] = useState<string | null>(null);

  // Validation errors
  const [weightError, setWeightError] = useState<string | null>(null);
  const [calError, setCalError] = useState<string | null>(null);
  const [burnError, setBurnError] = useState<string | null>(null);

  // Oura updating info tooltip
  const [showOuraUpdatingInfo, setShowOuraUpdatingInfo] = useState(false);

  // Existing entry tracking
  const [hasExistingEntry, setHasExistingEntry] = useState(false);
  const [loggedFields, setLoggedFields] = useState({ weight: false, cal: false, burn: false });
  const [loadedFieldValues, setLoadedFieldValues] = useState({ weight: "", cal: "", burn: "", burnMode: "total" as BurnInputMode });
  const [focusedField, setFocusedField] = useState<"weight" | "cal" | "burn" | null>(null);

  // Auth
  const [userId, setUserId] = useState<string | null>(null);
  const [showLogin, setShowLogin] = useState(false);

  // Oura connection state
  const [ouraConnection, setOuraConnection] = useState<OuraConnection | null>(null);
  // Source of the burn value as loaded from storage/DB (cleared when user starts editing)
  const [burnSource, setBurnSource] = useState<string | null>(null);
  // Remembers what source was loaded so we can show the override warning after user edits
  const originalBurnSourceRef = useRef<string | null>(null);

  const yesterday = useMemo(() => shiftDay(getTodayYmd(), -1), []);
  const today = getTodayYmd();

  // Auth state listener — updates userId live after login/logout
  useEffect(() => {
    if (!supabaseConfigured) return;
    // Initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUserId(session?.user?.id ?? null);
      if (session?.user) {
        getOuraConnection().then(setOuraConnection).catch(() => {});
      }
    });
    // Live updates
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUserId(session?.user?.id ?? null);
      if (session) {
        setShowLogin(false);
        getOuraConnection().then(setOuraConnection).catch(() => {});
      } else {
        setOuraConnection(null);
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  // Load existing entry whenever date/user changes.
  // If signed in, also check Supabase for this date so status dots reflect cloud truth.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const storage = getStorage();
      const raw = await storage.get(STORAGE_KEYS.entries);
      if (cancelled) return;
      const entries: GEntry[] = raw.value ? JSON.parse(raw.value) : [];
      let entry = entries.find((e) => e.date === date) ?? null;

      if (supabaseConfigured && userId) {
        const { data, error } = await supabase
          .from("entries")
          .select("date, weight, calories_in, active_calories, total_burned, net_calories, bmr, notes, source, updated_at, tdee_source, active_calories_source, total_burned_source")
          .eq("user_id", userId)
          .eq("date", date)
          .maybeSingle();

        if (!cancelled && !error && data) {
          const remoteEntry: GEntry = {
            date: data.date,
            weight: data.weight,
            caloriesIn: data.calories_in,
            activeCalories: data.active_calories,
            totalBurned: data.total_burned,
            netCalories: data.net_calories,
            bmr: data.bmr,
            notes: data.notes,
            source: data.source,
            tdeeSource: data.tdee_source,
            updatedAt: data.updated_at,
            activeCaloriesSource: data.active_calories_source,
            totalBurnedSource: data.total_burned_source,
          };

          const localUpdated = entry?.updatedAt ? Date.parse(entry.updatedAt) : NaN;
          const remoteUpdated = remoteEntry.updatedAt ? Date.parse(remoteEntry.updatedAt) : NaN;
          const useRemote = !entry || (!Number.isNaN(remoteUpdated) && (Number.isNaN(localUpdated) || remoteUpdated >= localUpdated));

          if (useRemote) {
            entry = remoteEntry;
          }

          const mergedEntries = [
            ...entries.filter((e) => e.date !== date),
            useRemote ? remoteEntry : entry,
          ].sort((a, b) => a.date.localeCompare(b.date));
          await storage.set(STORAGE_KEYS.entries, JSON.stringify(mergedEntries));
        }
      }

      if (cancelled) return;
      setFocusedField(null);
      if (entry) {
        const loadedWeight = entry.weight != null ? String(entry.weight) : "";
        const loadedCaloriesIn = formatWholeCalories(entry.caloriesIn);
        setWeight("");
        setCaloriesIn("");

        // Source resolution: oura/manual are known; inferred and null-with-value
        // are treated as "inferred" (Estimated tag) and "manual" (User tag) respectively.
        const totalSrc = entry.totalBurnedSource ?? null;
        const activeSrc = entry.activeCaloriesSource ?? null;
        const totalIsKnown = totalSrc === "oura" || totalSrc === "manual";
        const activeIsKnown = activeSrc === "oura" || activeSrc === "manual";
        const totalIsInferred = totalSrc === "inferred";
        const activeIsInferred = activeSrc === "inferred";
        // null-source + non-null value → treat as legacy/User
        const totalIsLegacy = totalSrc === null && entry.totalBurned != null;
        const activeIsLegacy = activeSrc === null && entry.activeCalories != null;

        if (entry.totalBurned != null && (totalIsKnown || totalIsInferred || totalIsLegacy)) {
          const loadedBurn = formatWholeCalories(entry.totalBurned);
          setMode("total");
          setBurnValue("");
          const src = totalIsInferred ? "inferred" : totalIsLegacy ? "manual" : totalSrc;
          setBurnSource(src);
          originalBurnSourceRef.current = src;
          setLoadedFieldValues({ weight: loadedWeight, cal: loadedCaloriesIn, burn: loadedBurn, burnMode: "total" });
        } else if (entry.activeCalories != null && (activeIsKnown || activeIsInferred || activeIsLegacy)) {
          const loadedBurn = formatWholeCalories(entry.activeCalories);
          setMode("active");
          setBurnValue("");
          const src = activeIsInferred ? "inferred" : activeIsLegacy ? "manual" : activeSrc;
          setBurnSource(src);
          originalBurnSourceRef.current = src;
          setLoadedFieldValues({ weight: loadedWeight, cal: loadedCaloriesIn, burn: loadedBurn, burnMode: "active" });
        } else {
          setBurnValue("");
          setBurnSource(null);
          originalBurnSourceRef.current = null;
          setLoadedFieldValues({ weight: loadedWeight, cal: loadedCaloriesIn, burn: "", burnMode: "total" });
        }

        setHasExistingEntry(true);
        setLoggedFields({
          weight: entry.weight != null,
          cal: entry.caloriesIn != null,
          burn: hasLoggedBurn(entry),
        });
      } else {
        setWeight("");
        setCaloriesIn("");
        setBurnValue("");
        setBurnSource(null);
        originalBurnSourceRef.current = null;
        setLoadedFieldValues({ weight: "", cal: "", burn: "", burnMode: "total" });
        setHasExistingEntry(false);
        setLoggedFields({ weight: false, cal: false, burn: false });
      }
      setShowOuraUpdatingInfo(false);
      setWeightError(null);
      setCalError(null);
      setBurnError(null);
      setSaveState("idle");
      setSaveError(null);
    };
    load();
    return () => { cancelled = true; };
  }, [date, userId]);

  const ouraConnected = !!ouraConnection?.enabled;

  const modeMeta =
    mode === "total"
      ? { label: "Total calories burned", placeholder: "e.g. 2800" }
      : { label: "Active calories burned", placeholder: "e.g. 550" };

  // Three-state source tag: Oura / User / Estimated / null
  // Oura waiting is only shown within the 7-day Oura sync window (today only for simplicity)
  const isToday = date === today;
  const burnTag: "Oura" | "User" | "Estimated" | "Updating" | null = (() => {
    if (burnSource === "oura") return "Oura";
    if (burnSource === "manual") return "User";
    // Oura takes priority over any inferred estimate for today
    if (ouraConnected && isToday) return ouraConnection?.lastSyncAt ? "Updating" : "Oura";
    if (burnSource === "inferred") return "Estimated";
    return null;
  })();

  // User started editing a field that was originally Oura-sourced
  const editingOuraOverride = originalBurnSourceRef.current === "oura" && burnSource === null;

  const burnHelper = (() => {
    if (burnSource === "oura") {
      return isToday
        ? "Synced from Oura • Updates throughout the day"
        : "Synced from Oura";
    }
    // Oura takes priority over inferred estimate for today
    if (ouraConnected && isToday && burnSource !== "manual") {
      return ouraConnection?.lastSyncAt ? "Oura syncs throughout the day" : "Waiting for Oura sync";
    }
    if (burnSource === "inferred") return "Estimated from your profile";
    if (!burnSource && !burnValue) return isToday ? "No calories burned logged yet" : null;
    return null;
  })();

  const validate = (): boolean => {
    let ok = true;
    if (weight.trim()) {
      const w = parseFloat(weight);
      if (!Number.isFinite(w) || w < 50 || w > 999) {
        setWeightError("Enter a valid weight between 50 and 999 lbs");
        ok = false;
      } else {
        setWeightError(null);
      }
    } else {
      setWeightError(null);
    }
    if (caloriesIn.trim()) {
      const c = parseFloat(caloriesIn);
      if (!Number.isFinite(c) || c <= 0 || c > 20000) {
        setCalError("Enter a valid calorie count (1 – 20,000)");
        ok = false;
      } else {
        setCalError(null);
      }
    } else {
      setCalError(null);
    }
    if (burnValue.trim()) {
      const b = parseFloat(burnValue);
      if (!Number.isFinite(b) || b <= 0 || b > 20000) {
        setBurnError("Enter a valid calorie count (1 – 20,000)");
        ok = false;
      } else {
        setBurnError(null);
      }
    } else {
      setBurnError(null);
    }
    return ok;
  };

  const handleSubmit = async (event?: FormEvent) => {
    event?.preventDefault();
    if (saveState === "loading") return;
    if (!validate()) return;

    setSaveState("loading");
    setSaveError(null);

    try {
      const parsedWeight = parseOptionalPositiveFloat(weight);
      const parsedCaloriesIn = parseOptionalPositiveInt(caloriesIn);
      const parsedBurn = parseOptionalPositiveInt(burnValue);

      const storage = getStorage();
      const raw = await storage.get(STORAGE_KEYS.entries);
      const entries: GEntry[] = raw.value ? JSON.parse(raw.value) : [];
      const existing = entries.find((e) => e.date === date) ?? null;

      const updated: GEntry = {
        date,
        weight: weight.trim() === "" ? (existing?.weight ?? null) : parsedWeight,
        caloriesIn: caloriesIn.trim() === "" ? (existing?.caloriesIn ?? null) : parsedCaloriesIn,
        activeCalories: mode === "active" ? (burnValue.trim() === "" ? (existing?.activeCalories ?? null) : parsedBurn) : (existing?.activeCalories ?? null),
        totalBurned: mode === "total" ? (burnValue.trim() === "" ? (existing?.totalBurned ?? null) : parsedBurn) : (existing?.totalBurned ?? null),
        activeCaloriesSource: mode === "active" && burnValue.trim() !== "" ? "manual" : (existing?.activeCaloriesSource ?? null),
        totalBurnedSource: mode === "total" && burnValue.trim() !== "" ? "manual" : (existing?.totalBurnedSource ?? null),
        netCalories: null,
        bmr: existing?.bmr ?? null,
        notes: existing?.notes ?? null,
        source: "mobile",
        updatedAt: new Date().toISOString(),
      };

      const newEntries = [
        ...entries.filter((e) => e.date !== date),
        updated,
      ].sort((a, b) => a.date.localeCompare(b.date));
      await storage.set(STORAGE_KEYS.entries, JSON.stringify(newEntries));

      if (supabaseConfigured && userId && !isDemoDataset([updated], [])) {
        await pushEntries(userId, [updated]);
      }

      setSaveState("saved");
      setHasExistingEntry(true);
      setLoggedFields({
        weight: updated.weight != null,
        cal: updated.caloriesIn != null,
        burn: updated.totalBurned != null || updated.activeCalories != null,
      });
      setWeight("");
      setCaloriesIn("");
      setBurnValue("");
      setFocusedField(null);
      setLoadedFieldValues({
        weight: updated.weight != null ? String(updated.weight) : "",
        cal: formatWholeCalories(updated.caloriesIn),
        burn: mode === "total" ? formatWholeCalories(updated.totalBurned) : formatWholeCalories(updated.activeCalories),
        burnMode: mode,
      });
      // Update burn source state to reflect the saved manual entry
      const savedBurnSrc = updated.totalBurnedSource ?? updated.activeCaloriesSource ?? null;
      setBurnSource(savedBurnSrc);
      originalBurnSourceRef.current = savedBurnSrc;
      setTimeout(() => setSaveState("idle"), 1200);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Save failed — try again.");
      setSaveState("error");
      setTimeout(() => setSaveState("idle"), 2500);
    }
  };

  const { weekday, monthDay } = formatPremiumDate(date);

  const saveButtonClass = [
    "flex h-14 w-full items-center justify-center gap-2 rounded-xl border border-blue-100/50 text-base font-semibold text-white transition-all duration-200",
    saveState === "loading" ? "opacity-80 cursor-not-allowed" : "",
    saveState === "saved"
      ? "bg-gradient-to-b from-emerald-400 to-emerald-600 shadow-[0_4px_20px_rgba(16,185,129,0.45)]"
      : saveState === "error"
      ? "bg-gradient-to-b from-red-400 to-red-600 shadow-[0_4px_16px_rgba(239,68,68,0.4)]"
      : "bg-gradient-to-b from-blue-400 to-blue-600 shadow-[0_4px_20px_rgba(59,130,246,0.45)] hover:translate-y-[-1px]",
  ]
    .filter(Boolean)
    .join(" ");

  const saveButtonLabel =
    saveState === "loading"
      ? "Saving…"
      : saveState === "saved"
      ? hasExistingEntry ? "Updated ✓" : "Saved ✓"
      : saveState === "error"
      ? "Error — try again"
      : hasExistingEntry ? "Update Entry" : "Log Entry";

  // Shared input className + style (fixes black-on-dark text on iOS via webkit)
  const inputCls = (errorActive: boolean) =>
    [
      "h-12 w-full rounded-xl border bg-slate-900/70 px-4 pr-16 text-base font-medium text-white placeholder:text-white/30 outline-none transition focus:ring-2",
      errorActive
        ? "border-red-400/60 ring-red-400/40"
        : "border-white/15 ring-blue-300/50",
    ].join(" ");
  const activeValueColor = "white";
  const ghostValueColor = "rgba(226,232,240,0.58)";
  const inputStyle: React.CSSProperties = {
    WebkitTextFillColor: activeValueColor,
    color: activeValueColor,
    caretColor: activeValueColor,
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0b1220] via-[#0f1a2e] to-[#0b1220] px-4 pb-[calc(1rem+env(safe-area-inset-bottom))] text-white">
      <style>{`
        .pt-safe { padding-top: max(1.25rem, env(safe-area-inset-top)); }
        .tdee-toggle button { -webkit-appearance: none; appearance: none; }
      `}</style>

      {showLogin && <LoginModal onClose={() => setShowLogin(false)} />}

      <form
        className="mx-auto flex w-full max-w-[420px] flex-col gap-3 pt-safe"
        onSubmit={handleSubmit}
      >
        {/* ── Header card ── */}
        <header className="rounded-2xl border border-white/[0.09] bg-gradient-to-b from-white/[0.07] to-white/[0.03] px-4 py-3 shadow-[0_8px_32px_rgba(0,0,0,0.5),inset_0_1px_0_rgba(255,255,255,0.08)] backdrop-blur-md">
          <div className="flex items-center justify-between gap-2">
            <div className="flex min-w-0 items-baseline gap-1.5">
              <p className="shrink-0 text-xs font-semibold text-white/75">Daily Entry</p>
              <span className="text-white/20 text-xs">·</span>
              <p className="truncate text-xs font-medium text-white/55">Weightplanner Pro</p>
            </div>

            {/* Auth chip — tappable when not signed in */}
            {userEmail ? (
              <div className="flex w-[120px] shrink-0 items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 py-1.5">
                <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-400" />
                <span className="truncate text-[0.72rem] text-white/60">{userEmail}</span>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setShowLogin(true)}
                className="flex shrink-0 items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 transition hover:bg-white/10 active:scale-95"
              >
                <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-red-400" />
                <span className="text-[0.72rem] text-white/60">Not signed in</span>
              </button>
            )}
          </div>

          {/* Large date display */}
          <p className="mt-2 text-[1.85rem] font-bold tracking-tight leading-none">
            {weekday}
            <span className="mx-2 font-light text-white/30">•</span>
            {monthDay}
          </p>

          {/* Date chips */}
          <div className="mt-1.5 flex gap-2 overflow-x-auto pb-0.5">
            <button
              className={`whitespace-nowrap rounded-full border px-4 py-2 text-sm font-medium transition ${
                date === today
                  ? "border-blue-300/50 bg-blue-400/25 text-blue-100"
                  : "border-white/10 bg-white/5 text-slate-200"
              }`}
              onClick={() => setDate(today)}
              type="button"
            >
              Today
            </button>
            <button
              className={`whitespace-nowrap rounded-full border px-4 py-2 text-sm font-medium transition ${
                date === yesterday
                  ? "border-blue-300/50 bg-blue-400/25 text-blue-100"
                  : "border-white/10 bg-white/5 text-slate-200"
              }`}
              onClick={() => setDate(yesterday)}
              type="button"
            >
              Yesterday
            </button>

            {/* Pick date — native input overlaid directly on button so iOS tap works */}
            <div className="relative">
              <button
                className={`flex items-center gap-1.5 whitespace-nowrap rounded-full border px-4 py-2 text-sm font-medium transition ${
                  date !== today && date !== yesterday
                    ? "border-blue-300/50 bg-blue-400/25 text-blue-100"
                    : "border-white/10 bg-white/5 text-slate-200"
                }`}
                type="button"
                onClick={() => {
                  const el = dateInputRef.current;
                  if (!el) return;
                  if (typeof (el as any).showPicker === "function") {
                    try { (el as any).showPicker(); } catch {}
                  } else {
                    el.focus();
                  }
                }}
              >
                <svg
                  className="h-3.5 w-3.5 text-slate-400"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  viewBox="0 0 24 24"
                >
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                  <line x1="16" y1="2" x2="16" y2="6" />
                  <line x1="8" y1="2" x2="8" y2="6" />
                  <line x1="3" y1="10" x2="21" y2="10" />
                </svg>
                {date !== today && date !== yesterday
                  ? new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" }).format(
                      new Date(`${date}T12:00:00`)
                    )
                  : "Pick date"}
              </button>
              {/* Hidden date input activated via showPicker() from button onClick */}
              <input
                ref={dateInputRef}
                type="date"
                value={date}
                onChange={(e) => e.target.value && setDate(e.target.value)}
                className="sr-only"
                style={{ colorScheme: "dark" }}
                tabIndex={-1}
              />
            </div>
          </div>
        </header>

        {/* ── Single fields card (all fields consolidated) ── */}
        <section className="overflow-hidden rounded-2xl border border-white/[0.12] bg-gradient-to-b from-white/[0.065] to-white/[0.035] shadow-[0_12px_36px_rgba(0,0,0,0.45),inset_0_1px_0_rgba(255,255,255,0.08)] backdrop-blur-md">

          {/* Weight */}
          <div className="px-4 py-3">
            <div className="mb-1.5 flex items-center justify-between">
              <label className="text-sm font-bold text-white" htmlFor="daily-weight">
                Weight (lbs)
              </label>
              {loggedFields.weight
                ? <span className="text-xs font-medium text-emerald-400">● logged</span>
                : <span className="text-xs text-slate-600">○ empty</span>}
            </div>
            <div className="relative">
              {loadedFieldValues.weight && !weight && focusedField !== "weight" && (
                <span className="pointer-events-none absolute left-4 right-16 top-1/2 -translate-y-1/2 truncate text-base font-medium" style={{ color: ghostValueColor }}>
                  {loadedFieldValues.weight}
                </span>
              )}
              <input
                className={inputCls(!!weightError)}
                id="daily-weight"
                type="text"
                inputMode="decimal"
                pattern="[0-9]*"
                onChange={(e) => { setWeight(e.target.value); setWeightError(null); }}
                onFocus={() => setFocusedField("weight")}
                onBlur={() => setFocusedField((prev) => (prev === "weight" ? null : prev))}
                onKeyDown={(e) => {
                  if (e.key !== "Enter") return;
                  e.preventDefault();
                  caloriesInInputRef.current?.focus();
                }}
                placeholder={loadedFieldValues.weight ? "" : "0.0"}
                style={inputStyle}
                value={weight}
              />
              <span className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full border border-white/10 bg-white/10 px-3 py-1 text-xs font-semibold text-slate-200">
                lbs
              </span>
            </div>
            {weightError && <p className="mt-2 text-xs text-red-400">{weightError}</p>}
          </div>

          <div className="border-t border-white/[0.06]" />

          {/* Calories In */}
          <div className="px-4 py-3">
            <div className="mb-1.5 flex items-center justify-between">
              <label className="text-sm font-bold text-white" htmlFor="daily-calories-in">
                Calories In
              </label>
              {loggedFields.cal
                ? <span className="text-xs font-medium text-emerald-400">● logged</span>
                : <span className="text-xs text-slate-600">○ empty</span>}
            </div>
            <div className="relative">
              {loadedFieldValues.cal && !caloriesIn && focusedField !== "cal" && (
                <span className="pointer-events-none absolute left-4 right-16 top-1/2 -translate-y-1/2 truncate text-base font-medium" style={{ color: ghostValueColor }}>
                  {loadedFieldValues.cal}
                </span>
              )}
              <input
                ref={caloriesInInputRef}
                className={inputCls(!!calError)}
                id="daily-calories-in"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                onChange={(e) => { setCaloriesIn(e.target.value); setCalError(null); }}
                onFocus={() => setFocusedField("cal")}
                onBlur={() => setFocusedField((prev) => (prev === "cal" ? null : prev))}
                onKeyDown={(e) => {
                  if (e.key !== "Enter") return;
                  e.preventDefault();
                  burnInputRef.current?.focus();
                }}
                placeholder={loadedFieldValues.cal ? "" : "e.g. 2200"}
                style={inputStyle}
                value={caloriesIn}
              />
              <span className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full border border-white/10 bg-white/10 px-3 py-1 text-xs font-semibold text-slate-200">
                cal
              </span>
            </div>
            {calError && <p className="mt-2 text-xs text-red-400">{calError}</p>}
          </div>

          <div className="border-t border-white/[0.06]" />

          {/* TDEE / Burn */}
          <div className="px-4 pb-4 pt-3">
            {/* Row 1: section title only */}
            <div className="mb-[10px]">
              <span className="text-sm font-semibold text-white/70">Calories Burned</span>
            </div>

            {/* Row 2: mode toggle — compact inline segmented pill */}
            <div className="mb-[12px] inline-flex w-fit rounded-full border border-white/[0.12] bg-white/[0.05] p-[3px]">
              <button
                className={`h-[34px] rounded-full px-[18px] text-[0.875rem] font-semibold transition-all ${
                  mode === "total"
                    ? "bg-white/[0.16] text-white shadow-[0_1px_4px_rgba(0,0,0,0.35)]"
                    : "bg-transparent text-white/40 hover:text-white/60"
                }`}
                onClick={() => setMode("total")}
                type="button"
              >
                Total
              </button>
              <button
                className={`h-[34px] rounded-full px-[18px] text-[0.875rem] font-semibold transition-all ${
                  mode === "active"
                    ? "bg-white/[0.16] text-white shadow-[0_1px_4px_rgba(0,0,0,0.35)]"
                    : "bg-transparent text-white/40 hover:text-white/60"
                }`}
                onClick={() => setMode("active")}
                type="button"
              >
                Active
              </button>
            </div>

            {/* Row 3: field label + source tag */}
            <div className="mb-1.5 flex items-center justify-between">
              <label className="text-sm font-bold text-white" htmlFor="daily-burn-input">
                {modeMeta.label}
              </label>
              {burnTag === "Oura" && (
                <span className="rounded-full border border-indigo-400/30 bg-indigo-400/20 px-2.5 py-0.5 text-[0.68rem] font-bold text-indigo-200">
                  Oura
                </span>
              )}
              {burnTag === "User" && (
                <span className="rounded-full border border-emerald-400/25 bg-emerald-400/[0.12] px-2.5 py-0.5 text-[0.68rem] font-bold text-emerald-300">
                  User
                </span>
              )}
              {burnTag === "Estimated" && (
                <span className="rounded-full border border-amber-400/25 bg-amber-400/[0.10] px-2.5 py-0.5 text-[0.68rem] font-bold text-amber-300">
                  Estimated
                </span>
              )}
              {burnTag === "Updating" && (
                <button
                  type="button"
                  onClick={() => setShowOuraUpdatingInfo((v) => !v)}
                  className="rounded-full border border-indigo-400/20 bg-indigo-400/10 px-2.5 py-0.5 text-[0.68rem] font-bold text-indigo-300/60"
                >
                  Updating
                </button>
              )}
            </div>
            {burnTag === "Updating" && showOuraUpdatingInfo && (
              <p className="mb-2 rounded-lg border border-indigo-400/20 bg-indigo-400/[0.08] px-3 py-2 text-xs text-indigo-200/60">
                Oura activity data is finalized at the end of the day. Your stats will appear here after your ring syncs tonight.
              </p>
            )}
            {editingOuraOverride && (
              <p className="mb-2 rounded-lg border border-amber-400/25 bg-amber-400/10 px-3 py-2 text-xs text-amber-300">
                Editing will override Oura's value for this entry.
              </p>
            )}
            {/* Row 4: input */}
            <div className="relative">
              {loadedFieldValues.burn && mode === loadedFieldValues.burnMode && !burnValue && focusedField !== "burn" && (
                <span className="pointer-events-none absolute left-4 right-16 top-1/2 -translate-y-1/2 truncate text-base font-medium" style={{ color: ghostValueColor }}>
                  {loadedFieldValues.burn}
                </span>
              )}
              <input
                ref={burnInputRef}
                className={inputCls(!!burnError)}
                id="daily-burn-input"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                onChange={(e) => {
                  setBurnValue(e.target.value);
                  setBurnError(null);
                  if (burnSource !== null) setBurnSource(null);
                }}
                onFocus={() => setFocusedField("burn")}
                onBlur={() => setFocusedField((prev) => (prev === "burn" ? null : prev))}
                onKeyDown={(e) => {
                  if (e.key !== "Enter") return;
                  e.preventDefault();
                  if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
                }}
                placeholder={loadedFieldValues.burn && mode === loadedFieldValues.burnMode ? "" : modeMeta.placeholder}
                style={inputStyle}
                value={burnValue}
              />
              <span className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full border border-white/10 bg-white/10 px-3 py-1 text-xs font-semibold text-slate-200">
                cal
              </span>
            </div>
            {burnError && <p className="mt-1.5 text-xs text-red-400">{burnError}</p>}
            {/* Row 5: helper text */}
            {burnHelper && (
              <p className="mt-[7px] text-xs leading-relaxed text-white/40">{burnHelper}</p>
            )}
          </div>

          <div className="border-t border-white/[0.06]" />

          <div className="px-4 pb-4 pt-3">
            <button
              className={saveButtonClass}
              disabled={saveState === "loading"}
              onClick={() => handleSubmit()}
              type="button"
            >
              {saveState === "loading" && (
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
              )}
              {saveButtonLabel}
            </button>
          </div>
        </section>

        {/* Error banner */}
        {saveError && (
          <div className="rounded-xl border border-red-400/30 bg-red-400/10 px-4 py-3 text-sm text-red-300">
            {saveError}
          </div>
        )}
      </form>
    </div>
  );
}

export default DailyEntryMobile;
